import { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
// import logo from "@packages/assets/logo.png";
import { QRCode } from "react-qrcode-logo";
import { useAppSelector } from "@packages/store/hooks";
import Modal from "@core/modal";
import { Button } from "@core/index";

interface AddStudentModalProps {
  show: boolean;
  onClose: () => void;
  uuid?: string;
  shouldUseModal?: boolean;
}

const AddStudentModal = ({
  show,
  onClose,
  uuid,
  shouldUseModal = true,
}: AddStudentModalProps) => {
  const { t } = useTranslation();
  const [selectedClassUUID, setSelectedClassUUID] = useState<
    string | undefined
  >(uuid);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Only update from props when the uuid is defined
  // This prevents overriding user selection with undefined
  useEffect(() => {
    if (uuid && uuid !== selectedClassUUID) {
      setSelectedClassUUID(uuid);
    }
  }, [uuid]);

  const onCloseHandler = () => {
    setSelectedClassUUID(undefined);
    onClose();
  };

  const classrooms = useAppSelector(
    (state) => state.user.teacher?.classes || [],
  );

  const sortedClassrooms = useMemo(() => {
    if (!classrooms || classrooms.length === 0) {
      return [];
    }
    return [...classrooms].sort((a, b) => b.id - a.id);
  }, [classrooms]);

  // If we have a selected class, render the inner component
  if (selectedClassUUID) {
    return (
      <AddStudentModalInner
        show={show}
        onClose={onCloseHandler}
        uuid={selectedClassUUID}
        shouldUseModal={shouldUseModal}
      />
    );
  }

  const content = (
    <div className="space-y-6 relative">
      <div
        ref={scrollContainerRef}
        className={`grid grid-cols-1 gap-4 max-h-[50vh] overflow-y-auto scroll-smooth ${
          shouldUseModal ? "grid-cols-1" : "grid-cols-2"
        }`}
      >
        {sortedClassrooms.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-gray-400">
            {t("students.noClassrooms")}
          </div>
        ) : (
          sortedClassrooms.map((classroom) => (
            <Button
              key={classroom.uuid}
              onClick={() => setSelectedClassUUID(classroom.uuid)}
              rounded="lg"
              bgColor="bg-white dark:bg-gray-800"
              variant="outline"
              size="md"
              className="border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 
              justify-between group hover:shadow-md animate-fadeIn"
            >
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400">
                  {classroom.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("common.studentCount")}: {classroom.studentCount}
                </p>
              </div>
              <div className="text-gray-400 dark:text-gray-500 group-hover:text-primary-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </Button>
          ))
        )}
        <div className="h-10" />
      </div>
      {sortedClassrooms.length > 5 && (
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pointer-events-none" />
      )}
    </div>
  );

  if (!shouldUseModal) {
    if (!show) {
      return null;
    }
    return content;
  }

  return (
    <Modal
      show={show}
      onClose={onCloseHandler}
      title={t("students.actions.add.title")}
      actions={[
        {
          label: t("common.cancel"),
          onClick: onCloseHandler,
          variant: "secondary",
        },
      ]}
    >
      {content}
    </Modal>
  );
};

const AddStudentModalInner = ({
  show,
  onClose,
  uuid,
  shouldUseModal = true,
}: AddStudentModalProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  // const joinLink = "student.madlen.io/join/" + uuid;
  const joinLink = uuid!;
  const downloadLink = "https://student.madlen.io/okul";

  const handleCopyLink = (link: string) => {
    navigator.clipboard.writeText(link);
    setCopied(true);
  };

  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [copied]);

  const content = (
    <div className="flex flex-col items-center space-y-6">
      <div className="w-full flex items-center justify-center mb-6 relative">
        <div className="flex items-center">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              currentStep >= 1 ? "bg-primary-500" : "bg-gray-300"
            } text-white font-bold`}
          >
            1
          </div>
          <div className="w-20 h-1 mx-2 bg-gray-300"></div>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              currentStep >= 2 ? "bg-primary-500" : "bg-gray-300"
            } text-white font-bold`}
          >
            2
          </div>
          <Button
            onClick={() => {
              window.open("https://student.madlen.io/help", "_blank");
            }}
            textColor="text-white"
            bgColor="bg-purple-500 hover:bg-purple-600"
            rounded="lg"
            size="sm"
            className="absolute top-0 left-0"
          >
            {t("common.help")}
          </Button>
        </div>
      </div>

      {currentStep === 1 ? (
        <div className="flex flex-col items-center space-y-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            {t("students.actions.add.downloadApp")}
          </h3>
          <p className="text-center text-gray-600 dark:text-gray-300">
            {t("students.actions.add.downloadAppDesc")}
          </p>
          <div className="border-4 sm:border-8 rounded-xl sm:rounded-2xl border-[#9178dd] bg-white p-1">
            <QRCode
              value={downloadLink}
              size={220}
              // logoImage={logo}
              // removeQrCodeBehindLogo={true}
              fgColor="#9178dd"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-sm justify-center">
            <Button
              onClick={() =>
                window.open(
                  "https://apps.apple.com/tr/app/madlen-okul/id6741554783",
                  "_blank",
                )
              }
              textColor="text-white"
              bgColor="bg-black hover:bg-gray-900"
              rounded="lg"
              size="md"
              className="flex items-center justify-center gap-2"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
              </svg>
              App Store
            </Button>
            <Button
              onClick={() =>
                window.open(
                  "https://play.google.com/store/apps/details?id=com.madlen.madlenexam",
                  "_blank",
                )
              }
              textColor="text-white"
              bgColor="bg-green-600 hover:bg-green-700"
              rounded="lg"
              size="md"
              className="flex items-center justify-center gap-2"
            >
              <svg
                className="w-6 h-6"
                version="1.1"
                id="Layer_1"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                viewBox="0 0 511.999 511.999"
                xmlSpace="preserve"
              >
                <g>
                  <path
                    style={{ fill: "#32BBFF" }}
                    d="M382.369,175.623C322.891,142.356,227.427,88.937,79.355,6.028
		C69.372-0.565,57.886-1.429,47.962,1.93l254.05,254.05L382.369,175.623z"
                  />
                  <path
                    style={{ fill: "#32BBFF" }}
                    d="M47.962,1.93c-1.86,0.63-3.67,1.39-5.401,2.308C31.602,10.166,23.549,21.573,23.549,36v439.96
		c0,14.427,8.052,25.834,19.012,31.761c1.728,0.917,3.537,1.68,5.395,2.314L302.012,255.98L47.962,1.93z"
                  />
                  <path
                    style={{ fill: "#32BBFF" }}
                    d="M302.012,255.98L47.956,510.035c9.927,3.384,21.413,2.586,31.399-4.103
		c143.598-80.41,237.986-133.196,298.152-166.746c1.675-0.941,3.316-1.861,4.938-2.772L302.012,255.98z"
                  />
                </g>
                <path
                  style={{ fill: "#2C9FD9" }}
                  d="M23.549,255.98v219.98c0,14.427,8.052,25.834,19.012,31.761c1.728,0.917,3.537,1.68,5.395,2.314
	L302.012,255.98H23.549z"
                />
                <path
                  style={{ fill: "#29CC5E" }}
                  d="M79.355,6.028C67.5-1.8,53.52-1.577,42.561,4.239l255.595,255.596l84.212-84.212
	C322.891,142.356,227.427,88.937,79.355,6.028z"
                />
                <path
                  style={{ fill: "#D93F21" }}
                  d="M298.158,252.126L42.561,507.721c10.96,5.815,24.939,6.151,36.794-1.789
	c143.598-80.41,237.986-133.196,298.152-166.746c1.675-0.941,3.316-1.861,4.938-2.772L298.158,252.126z"
                />
                <path
                  style={{ fill: "#FFD500" }}
                  d="M488.45,255.98c0-12.19-6.151-24.492-18.342-31.314c0,0-22.799-12.721-92.682-51.809l-83.123,83.123
	l83.204,83.205c69.116-38.807,92.6-51.892,92.6-51.892C482.299,280.472,488.45,268.17,488.45,255.98z"
                />
                <path
                  style={{ fill: "#FFAA00" }}
                  d="M470.108,287.294c12.191-6.822,18.342-19.124,18.342-31.314H294.303l83.204,83.205
	C446.624,300.379,470.108,287.294,470.108,287.294z"
                />
              </svg>
              Play Store
            </Button>
          </div>
          <Button
            onClick={() => setCurrentStep(2)}
            textColor="text-white"
            bgColor="bg-primary-500 hover:bg-primary-600"
            rounded="lg"
            size="md"
            className="mt-4"
          >
            {t("students.actions.add.studentsDownloadedContinue")}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col items-center space-y-4">
          <h3 className="text-lg text-center font-semibold text-gray-800 dark:text-gray-200">
            {t("students.actions.add.joinClass")}
          </h3>
          <p className="text-center text-gray-600 dark:text-gray-300">
            {t("students.actions.add.joinInstructions")}
          </p>

          {/* <div className="border-4 sm:border-8 rounded-xl sm:rounded-2xl border-primary-600 bg-white p-1">
            <QRCode
              value={joinLink}
              size={220}
              logoImage={logo}
              removeQrCodeBehindLogo={true}
              fgColor="#d07e39"
            />
          </div> */}

          <div
            className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 p-3 rounded-lg cursor-pointer"
            onClick={() => handleCopyLink(joinLink)}
          >
            <span className="text-gray-800 dark:text-gray-200 flex-1 px-4">
              {joinLink}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {copied ? t("common.copied") : t("common.copy")}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              onClick={() => setCurrentStep(1)}
              textColor="text-white"
              bgColor="bg-gray-500 hover:bg-gray-600"
              rounded="lg"
              size="md"
            >
              {t("common.back")}
            </Button>
            <Button
              onClick={() => {
                onClose();
              }}
              textColor="text-white"
              bgColor="bg-primary-500 hover:bg-primary-600"
              rounded="lg"
              size="md"
            >
              {t("common.finish")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  if (!shouldUseModal) {
    if (!show) {
      return null;
    }
    return (
      <div className="max-w-xl mx-auto border p-2 rounded-lg">
        {content}
        <div className="h-6" />
      </div>
    );
  }

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={t("students.actions.add.title")}
    >
      {content}
    </Modal>
  );
};

export default AddStudentModal;
